import { useEffect, useMemo, useRef, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useCentralStore } from "../../store/Central";
import { ArticleForm } from "./article-form.component";
import { Logger } from "@/lib/logger/Logger";
import { ExtendedArticleEntityType } from "../../store/Jobs/types";
import {
	ArticleEntityType,
	ArticleEntityTypeInsert,
	SupabaseTableEnum,
} from "@/lib/supabase/supabaseTypes";
import { showNotification } from "../../store/Central/selectors";
import { supabase } from "@/lib/supabase/supabaseClient";
import { useTemplatesStore } from "../../store/Templates";
import { ArticleCodeChangeDialog } from "../../components/template-dialogs/article-code-change-dialog.component";

const AUTOSAVE_DEBOUNCE_MS = 500;

interface ArticleDetailProps {
	article: ExtendedArticleEntityType;
	/** Called when editing switches to another article (default-article copy). */
	onSwitchArticle: (article: { id: number }) => void;
}

export const ArticleDetail: React.FC<ArticleDetailProps> = ({
	article,
	onSwitchArticle,
}) => {
	const { organization, getArticles } = useCentralStore((state) => ({
		getArticles: state.getArticles,
		organization: state.organization,
	}));

	const handleAddArticle = async (article: ArticleEntityTypeInsert) => {
		const { data, error } = await supabase
			.from(SupabaseTableEnum.ARTICLES)
			.insert(article)
			.select()
			.single();

		if (error) {
			Logger.error(error);
			showNotification({
				message: "Artikel konnte nicht hinzugefügt werden",
				type: "error",
			});
			return;
		}

		await getArticles();
		onSwitchArticle(data);
	};

	const handleUpdateArticle = async (
		article: ExtendedArticleEntityType,
		silent = false
	) => {
		// remove article.custom as it is not a column in the db
		const { custom, is_new, ...filteredArticle } = article;
		const newArticle: ArticleEntityType = {
			...filteredArticle,
			organization_id: organization?.id as string,
		};

		// if custom is false, we need to create a new user article (without id)
		if (custom === false) {
			const newArticleWithoutId: ArticleEntityTypeInsert = {
				...newArticle,
				id: undefined,
			};
			await handleAddArticle(newArticleWithoutId);
		} else {
			// otherwise, we update the existing article (with id)
			const { error } = await supabase
				.from(SupabaseTableEnum.ARTICLES)
				.update(newArticle)
				.eq("id", article?.id);

			if (error) {
				Logger.error(error);
				showNotification({
					message: "Artikel konnte nicht aktualisiert werden",
					type: "error",
				});
				return;
			}

			await getArticles();
			if (!silent) {
				showNotification({
					message: "Artikel erfolgreich aktualisiert",
					type: "success",
				});
			}
		}
	};

	const defaultValues = useMemo(() => ({ ...article }), [article]);

	const methods = useForm({
		defaultValues,
	});

	useEffect(() => {
		// keepDirtyValues so a store refresh after a save never clobbers
		// fields the user is still editing (safe: remounted per article via key)
		methods.reset(defaultValues, { keepDirtyValues: true });
	}, [methods.reset, defaultValues]);

	const [codeChangeDialogOpen, setCodeChangeDialogOpen] = useState(false);
	const [affectedTemplateIds, setAffectedTemplateIds] = useState<number[]>(
		[]
	);
	const [pendingCodeChange, setPendingCodeChange] = useState<{
		oldCode: string;
		newCode: string;
		formData: any;
	} | null>(null);
	const { removeItemFromTemplates, checkTemplateUsage } = useTemplatesStore(
		(state) => ({
			removeItemFromTemplates: state.removeItemFromTemplates,
			checkTemplateUsage: state.checkTemplateUsage,
		})
	);

	const submitArticle = async (data: any, silent: boolean) => {
		const codeChanged = data.code_e !== article.code_e;

		if (codeChanged) {
			const templateIds = await checkTemplateUsage({
				article_id: article.id,
			});
			if (templateIds.length > 0) {
				setAffectedTemplateIds(templateIds);
				setPendingCodeChange({
					oldCode: article.code_e,
					newCode: data.code_e,
					formData: data,
				});
				setCodeChangeDialogOpen(true);
				return;
			}
		}

		await handleUpdateArticle({ ...data }, silent);
	};

	const onSubmit = async (data: any) => submitArticle(data, false);

	// Autosave: debounce while typing, flush pending changes on unmount.
	const submitRef = useRef(submitArticle);
	submitRef.current = submitArticle;
	const isDirtyRef = useRef(false);
	isDirtyRef.current = methods.formState.isDirty;
	const autosaveTimerRef = useRef<number>();

	useEffect(() => {
		const subscription = methods.watch((_, { type }) => {
			if (type !== "change") return;
			window.clearTimeout(autosaveTimerRef.current);
			autosaveTimerRef.current = window.setTimeout(() => {
				methods.handleSubmit((data) => submitRef.current(data, true))();
			}, AUTOSAVE_DEBOUNCE_MS);
		});
		return () => subscription.unsubscribe();
	}, [methods]);

	useEffect(() => {
		return () => {
			window.clearTimeout(autosaveTimerRef.current);
			if (isDirtyRef.current) {
				methods.handleSubmit((data) => submitRef.current(data, true))();
			}
		};
	}, [methods]);

	// A reload or tab close would lose changes the autosave has not written
	// yet — let the browser show its native "unsaved changes" warning.
	useEffect(() => {
		const handleBeforeUnload = (event: BeforeUnloadEvent) => {
			if (!isDirtyRef.current) return;
			event.preventDefault();
			event.returnValue = "";
		};
		window.addEventListener("beforeunload", handleBeforeUnload);
		return () =>
			window.removeEventListener("beforeunload", handleBeforeUnload);
	}, []);

	const handleCodeChangeKeepInTemplates = async () => {
		if (!pendingCodeChange) return;
		setCodeChangeDialogOpen(false);
		await handleUpdateArticle({ ...pendingCodeChange.formData });
		setPendingCodeChange(null);
	};

	const handleCodeChangeRemoveFromTemplates = async () => {
		if (!pendingCodeChange) return;
		await removeItemFromTemplates({ article_id: article.id });
		setCodeChangeDialogOpen(false);
		await handleUpdateArticle({ ...pendingCodeChange.formData });
		setPendingCodeChange(null);
	};

	const handleCodeChangeCancel = () => {
		setCodeChangeDialogOpen(false);
		setPendingCodeChange(null);
	};

	return (
		<>
			<div className="px-4 pb-5 pt-4">
				<FormProvider {...methods}>
					<ArticleForm onSubmit={onSubmit} />
				</FormProvider>
			</div>
			<ArticleCodeChangeDialog
				open={codeChangeDialogOpen}
				onOpenChange={setCodeChangeDialogOpen}
				templateIds={affectedTemplateIds}
				oldCode={pendingCodeChange?.oldCode ?? ""}
				newCode={pendingCodeChange?.newCode ?? ""}
				onKeepInTemplates={handleCodeChangeKeepInTemplates}
				onRemoveFromTemplates={handleCodeChangeRemoveFromTemplates}
				onCancel={handleCodeChangeCancel}
			/>
		</>
	);
};
